/**
 * The recipe book.
 *
 * Rewritten after the honest verdict that the first version was beige —
 * "banana porridge, bro, who's doing all that". It was written to be *safe*,
 * matching a generic fridge, and safe came out as food nobody opens an app for.
 *
 * This version is built on what people actually cook and post: bowls dominate,
 * protein is the headline, and almost nothing needs technique. The formats came
 * from real sources rather than an impression of the genre — bowls were roughly
 * fifteen of forty in one popular dinner round-up, and the macro shape of the
 * viral ones sits at 350–500 calories and 30–40g of protein per serving, which
 * is where these land too.
 *
 * `protein` and `calories` are per-serving estimates from typical quantities.
 * They exist so bulk / cut / maintain can rank rather than merely filter, and
 * so a meal can be judged at a glance. They are a label, not nutrition
 * tracking, and they are not precise enough to be anything else.
 *
 * Every step has two depths — `text` for someone who has cooked before,
 * `detail` for someone who hasn't — and `notes` is technique, shown only at the
 * advanced level.
 *
 * Sources for the formats and macro shape:
 *   eatingbirdfood.com/viral-beef-cottage-cheese-bowl
 *   ohsnapmacros.com/high-protein-overnight-oats
 *   ohsnapmacros.com/teriyaki-salmon-bowls
 *   pinchofyum.com/high-protein-dinners
 */

import type { Recipe } from "./types";

export const RECIPES: Recipe[] = [
  // ---------------------------------------------------------------- bowls --
  {
    id: "beef-cottage-cheese-bowl",
    title: "Cottage cheese beef bowl",
    ingredients: ["beef", "sweet potato", "cottage cheese", "olive oil"],
    minutes: 30,
    serves: 4,
    protein: 33,
    calories: 459,
    diet: "omnivore",
    steps: [
      {
        text: "Cube the sweet potato, toss with oil and salt, roast at 200°C for 20 minutes.",
        detail: "Spread them out with space between. Piled up they steam instead of roasting, and you get soft grey cubes.",
      },
      {
        text: "Brown the mince hard in a dry pan, then season heavily.",
        detail: "Don't stir it for the first two minutes. The browning is the flavour, and constant stirring keeps it grey.",
      },
      {
        text: "Build the bowl: potato, beef, a big spoon of cottage cheese on top.",
        detail: "The cottage cheese goes on last and cold — it's doing the job sour cream would, and melting it wastes the point.",
      },
    ],
    notes: [
      "The cottage cheese is why this works as a macro dish: it adds around 12g of protein per bowl for about 80 calories.",
      "Hot honey or a chilli crisp over the top is what most versions of this do and it is worth it — the dish is fatty and salty and wants something sharp.",
      "90/10 mince rather than 80/20 keeps it near 450 calories. 80/20 pushes it past 550 without adding protein.",
    ],
    tags: ["bowl", "high-protein", "meal-prep"],
    difficulty: "beginner",
  },
  {
    id: "chicken-rice-bowl",
    title: "Chicken and rice bowl",
    ingredients: ["chicken", "rice", "onion", "olive oil"],
    minutes: 30,
    serves: 2,
    protein: 44,
    calories: 520,
    diet: "omnivore",
    steps: [
      {
        text: "Start the rice.",
        detail: "Two parts water to one of rice, lid on, lowest heat, twelve minutes, then off the heat and left alone for five more. Lifting the lid early is how it ends up crunchy.",
      },
      {
        text: "Season the chicken well and sear it without moving it.",
        detail: "It will stick, then release by itself once a crust forms. Poking at it is what tears it.",
      },
      {
        text: "Add the sliced onion, turn the heat down, and cook through.",
        detail: "Thighs are done at about 12 minutes and forgiving; breast needs 8 and is not. No pink in the thickest part.",
      },
      { text: "Rest five minutes, slice, and pile it on the rice with the pan onions." },
    ],
    notes: [
      "The fond in the pan is the best part — a splash of water or stock and it becomes a sauce.",
      "Thighs at 82°C beat thighs at 75°C: the connective tissue has actually rendered. Breast past 68°C is finished in the wrong sense.",
      "Cooked and cooled rice forms resistant starch, which is why this reheats better than it has any right to.",
    ],
    tags: ["bowl", "high-protein", "meal-prep"],
    difficulty: "beginner",
  },
  {
    id: "teriyaki-salmon-bowl",
    title: "Teriyaki salmon bowl",
    ingredients: ["salmon", "rice", "carrots", "peas"],
    minutes: 25,
    serves: 4,
    protein: 30,
    calories: 382,
    diet: "omnivore",
    steps: [
      {
        text: "Cube the salmon and marinate it for ten minutes, no longer.",
        detail: "Longer and the acid starts curing it — the texture goes soft and slightly mushy, and it never recovers.",
      },
      {
        text: "Bake at 200°C for 10–12 minutes.",
        detail: "Done when it flakes under light pressure and is still just translucent in the middle. Opaque all through is overdone.",
      },
      { text: "Reduce the leftover marinade in a pan until it coats a spoon." },
      { text: "Rice in the bowl, veg, salmon, sauce over the top." },
    ],
    notes: [
      "Never pour unreduced marinade over cooked fish — it has raw salmon in it. Two minutes at a simmer fixes that and thickens it at the same time.",
      "Swapping half the rice for cauliflower rice takes this to roughly 300 calories with the protein untouched, which is the standard cut version.",
    ],
    tags: ["bowl", "high-protein", "quick"],
    difficulty: "intermediate",
  },
  {
    id: "burrito-bowl",
    title: "Beef burrito bowl",
    ingredients: ["beef", "rice", "beans", "tomatoes", "cheese"],
    minutes: 25,
    serves: 4,
    protein: 38,
    calories: 545,
    diet: "omnivore",
    steps: [
      { text: "Brown the mince, then add cumin, paprika and chilli and cook them in the fat for a minute." },
      {
        text: "Add the beans and chopped tomatoes and simmer ten minutes.",
        detail: "It wants to be thick enough to sit on a spoon. Soupy means it soaks the rice and the bowl collapses.",
      },
      { text: "Over rice, with the cheese on top while it's hot." },
    ],
    notes: [
      "Frying the spices in the rendered beef fat rather than stirring them into liquid is the entire difference between this and a bland mince.",
      "Lime at the end, off the heat. Acid is what makes it taste finished rather than heavy.",
    ],
    tags: ["bowl", "high-protein", "meal-prep", "cheap"],
    difficulty: "beginner",
  },
  {
    id: "chicken-fajita-bowl",
    title: "Chicken fajita bowl",
    ingredients: ["chicken", "onion", "tomatoes", "rice", "cheese"],
    minutes: 30,
    serves: 3,
    protein: 41,
    calories: 470,
    diet: "omnivore",
    steps: [
      { text: "Slice the chicken and toss it with paprika, cumin and salt." },
      {
        text: "Sear it in a very hot pan, in two batches.",
        detail: "One crowded pan steams. Two quick batches take the same time and taste completely different.",
      },
      { text: "Same pan, cook the sliced onion until it takes some colour, then return the chicken." },
      { text: "Serve over rice with the tomatoes and cheese." },
    ],
    notes: [
      "A cast-iron pan left on high for three minutes before anything goes in is most of what makes fajitas taste like fajitas.",
      "Chicken thigh here rather than breast costs about 60 calories a serving and is much harder to ruin.",
    ],
    tags: ["bowl", "high-protein", "quick"],
    difficulty: "beginner",
  },
  {
    id: "tuna-rice-bowl",
    title: "Spicy tuna rice bowl",
    ingredients: ["tuna", "rice", "yoghurt", "cucumber"],
    minutes: 10,
    serves: 1,
    protein: 42,
    calories: 430,
    diet: "omnivore",
    steps: [
      {
        text: "Mix the tuna with yoghurt, sriracha and a little soy.",
        detail: "Greek yoghurt stands in for mayo here — same job, about a fifth of the calories and it adds protein instead of none.",
      },
      { text: "Spoon over warm rice, cucumber on the side." },
    ],
    notes: [
      "Warm rice under cold topping is the whole appeal; it is worth microwaving day-old rice rather than eating it fridge-cold.",
      "Tinned tuna in spring water rather than oil saves roughly 90 calories and loses nothing here, because the yoghurt is providing the richness.",
    ],
    tags: ["bowl", "high-protein", "quick", "no-cook"],
    difficulty: "beginner",
  },

  // ------------------------------------------------------------ breakfast --
  {
    id: "protein-oats",
    title: "Protein oats",
    ingredients: ["oats", "milk", "protein powder", "bananas"],
    minutes: 6,
    serves: 1,
    protein: 35,
    calories: 420,
    diet: "vegetarian",
    steps: [
      { text: "Cook the oats in milk, stirring, for about five minutes." },
      {
        text: "Take the pan off the heat, wait a minute, then stir the protein powder in.",
        detail: "Off the heat and cooled slightly, or the whey seizes and you get lumps of rubber through it. This is the single thing people get wrong.",
      },
      { text: "Mash half a banana in, slice the rest on top." },
    ],
    notes: [
      "Casein or a whey/casein blend survives heat far better than pure whey and gives a thicker bowl.",
      "A pinch of salt is not optional. Unsalted oats taste of nothing and people blame the protein powder.",
    ],
    tags: ["breakfast", "high-protein", "quick"],
    difficulty: "beginner",
  },
  {
    id: "overnight-oats",
    title: "Overnight oats",
    ingredients: ["oats", "yoghurt", "milk", "protein powder"],
    minutes: 5,
    serves: 1,
    protein: 35,
    calories: 359,
    diet: "vegetarian",
    steps: [
      { text: "Everything in a jar. Shake it." },
      {
        text: "Fridge, at least four hours.",
        detail: "Under four and the oats are still chalky in the middle. Overnight is the name for a reason.",
      },
      { text: "Loosen with a splash more milk in the morning." },
    ],
    notes: [
      "A tablespoon of chia thickens it properly and adds fibre; without it the mix stays loose no matter how long it sits.",
      "Make four jars at once. This is the format's whole advantage and it survives three days easily.",
    ],
    tags: ["breakfast", "high-protein", "meal-prep", "no-cook"],
    difficulty: "beginner",
  },
  {
    id: "egg-white-scramble",
    title: "Egg white scramble",
    ingredients: ["eggs", "spinach", "cheese"],
    minutes: 8,
    serves: 1,
    protein: 32,
    calories: 240,
    diet: "vegetarian",
    steps: [
      {
        text: "Wilt the spinach in a dry pan and squeeze the water out.",
        detail: "Skipping the squeeze is why egg dishes go watery. A surprising amount comes out.",
      },
      {
        text: "Whites into the pan on a low heat, stirring constantly.",
        detail: "Whites go rubbery faster than whole eggs — noticeably faster. Low heat and off early.",
      },
      { text: "Spinach and cheese folded through at the end." },
    ],
    notes: [
      "Whites alone have no fat, so they need either a non-stick pan or a little oil regardless of the macros.",
      "One whole egg among the whites costs about 70 calories and transforms both texture and taste. Worth it unless you're near the end of a cut.",
    ],
    tags: ["breakfast", "high-protein", "cut", "quick"],
    difficulty: "beginner",
  },
  {
    id: "yoghurt-protein-bowl",
    title: "Greek yoghurt bowl",
    ingredients: ["yoghurt", "berries", "oats"],
    minutes: 3,
    serves: 1,
    protein: 28,
    calories: 310,
    diet: "vegetarian",
    steps: [
      { text: "Greek yoghurt in a bowl, berries and oats on top." },
      {
        text: "Crush a few berries into it with a fork.",
        detail: "The released juice sweetens the whole bowl, so you need less honey, or none.",
      },
    ],
    notes: [
      "Full-fat Greek is roughly 130 calories per 100g against 60 for 0%, with nearly the same protein. That gap is the whole decision.",
      "Frozen berries thaw into it in ten minutes and cost a third of fresh.",
    ],
    tags: ["breakfast", "high-protein", "quick", "no-cook"],
    difficulty: "beginner",
  },
  {
    id: "cottage-cheese-toast",
    title: "Cottage cheese toast",
    ingredients: ["cottage cheese", "bread", "eggs"],
    minutes: 8,
    serves: 1,
    protein: 30,
    calories: 350,
    diet: "vegetarian",
    steps: [
      { text: "Toast the bread properly — dark, not pale." },
      {
        text: "Cottage cheese on thick, egg on top however you like it.",
        detail: "Pale toast under a wet topping goes soft in seconds. The extra minute in the toaster is doing structural work.",
      },
    ],
    notes: [
      "Salt and a lot of black pepper. Cottage cheese is bland on its own and this is the whole seasoning.",
      "Chilli crisp or hot honey over it is what makes this a meal rather than a diet food.",
    ],
    tags: ["breakfast", "high-protein", "quick"],
    difficulty: "beginner",
  },

  // -------------------------------------------------------------- skillet --
  {
    id: "beef-and-rice-skillet",
    title: "Ground beef and rice skillet",
    ingredients: ["beef", "rice", "onion", "tomatoes"],
    minutes: 30,
    serves: 4,
    protein: 36,
    calories: 510,
    diet: "omnivore",
    steps: [
      { text: "Brown the mince, remove it, soften the onion in the fat left behind." },
      { text: "Rice into the pan, stir it in the fat for a minute, then add the tomatoes and water." },
      {
        text: "Lid on, low heat, 15 minutes. Beef back in at the end.",
        detail: "Don't stir it while the rice cooks. Stirring releases starch and turns it into risotto, which is a different and stickier dinner.",
      },
    ],
    notes: [
      "One pan, one hob ring, and it scales to eight servings without changing anything. This is the batch-cook backbone.",
      "Toasting the rice in the beef fat before the liquid keeps the grains separate and adds a nutty note you can't get afterwards.",
    ],
    tags: ["skillet", "high-protein", "meal-prep", "cheap"],
    difficulty: "beginner",
  },
  {
    id: "chicken-pasta-skillet",
    title: "Chicken and spinach pasta",
    ingredients: ["chicken", "pasta", "spinach", "cheese"],
    minutes: 25,
    serves: 3,
    protein: 45,
    calories: 560,
    diet: "omnivore",
    steps: [
      { text: "Boil the pasta in well-salted water. Keep a mug of the water back." },
      { text: "Sear the sliced chicken, then add the spinach to wilt." },
      {
        text: "Pasta into the pan with a splash of the water and the cheese.",
        detail: "The starch in that water is what makes the sauce cling instead of sliding to the bottom of the bowl.",
      },
    ],
    notes: [
      "Finish the pasta a minute short and let it end in the pan. It absorbs sauce rather than being coated in it.",
      "Higher-protein pasta — chickpea or lentil — takes this past 55g a serving and the sauce hides the texture difference.",
    ],
    tags: ["skillet", "high-protein", "quick"],
    difficulty: "beginner",
  },
  {
    id: "shakshuka",
    title: "Shakshuka",
    ingredients: ["eggs", "tomatoes", "onion", "bread"],
    minutes: 30,
    serves: 2,
    protein: 24,
    calories: 380,
    diet: "vegetarian",
    steps: [
      { text: "Soften the onion, add cumin and paprika, cook a minute, then the tomatoes." },
      {
        text: "Simmer fifteen minutes until thick.",
        detail: "Thick enough that a spoon leaves a channel. Watery sauce and the eggs sink and poach unevenly.",
      },
      {
        text: "Wells in the sauce, an egg in each, lid on for 6–8 minutes.",
        detail: "Judge by the whites — no clear liquid left. The yolks keep setting in the hot sauce after it's off the heat.",
      },
    ],
    notes: [
      "The lid is what cooks the tops. Without it you overcook the bottoms waiting for the whites.",
      "Feta crumbled over at the end adds about 6g of protein a serving and the salt it needs anyway.",
    ],
    tags: ["skillet", "brunch", "vegetarian"],
    difficulty: "intermediate",
  },
  {
    id: "veg-fried-rice",
    title: "Egg fried rice",
    ingredients: ["rice", "eggs", "peas", "carrots", "onion"],
    minutes: 15,
    serves: 2,
    protein: 22,
    calories: 420,
    diet: "vegetarian",
    steps: [
      {
        text: "Use cold, cooked rice — yesterday's.",
        detail: "Fresh rice steams and goes to mush. If you must, spread it on a tray to dry and cool first.",
      },
      { text: "Very hot pan. Onion and carrot two minutes." },
      { text: "Push aside, scramble the eggs in the space, then mix through." },
      {
        text: "Rice and peas in, fry hard for four minutes.",
        detail: "Spread it and leave it between stirs. Constant tossing keeps the pan cool and you never get the toasted bits.",
      },
    ],
    notes: [
      "Soy down the side of the pan, not over the rice: it hits hot metal, caramelises, and tastes of more than salt.",
      "Two extra eggs takes this to 34g of protein for 140 more calories, which is usually the right trade.",
    ],
    tags: ["skillet", "quick", "leftovers", "cheap"],
    difficulty: "intermediate",
  },

  // ------------------------------------------------------------- traybake --
  {
    id: "chicken-traybake",
    title: "Chicken and potato traybake",
    ingredients: ["chicken", "potatoes", "onion", "olive oil"],
    minutes: 50,
    serves: 4,
    protein: 42,
    calories: 490,
    diet: "omnivore",
    steps: [
      {
        text: "Potatoes in first, oiled and salted, 200°C for 20 minutes.",
        detail: "They need the head start. Chicken added at the same time would be dry long before they're done.",
      },
      { text: "Add the chicken and onion, another 25 minutes." },
      { text: "Everything on one tray, nothing to wash up." },
    ],
    notes: [
      "One layer, always. Two layers of anything on a tray and the top roasts while the bottom boils.",
      "Bone-in thighs are cheaper, harder to overcook, and give you the rendered fat that makes the potatoes worth eating.",
    ],
    tags: ["traybake", "one-pan", "high-protein", "meal-prep"],
    difficulty: "beginner",
  },
  {
    id: "salmon-traybake",
    title: "Salmon traybake",
    ingredients: ["salmon", "potatoes", "tomatoes", "olive oil"],
    minutes: 45,
    serves: 2,
    protein: 38,
    calories: 520,
    diet: "omnivore",
    steps: [
      { text: "Halved potatoes, oiled and salted, 200°C for 25 minutes." },
      { text: "Add the tomatoes and the salmon." },
      {
        text: "12–15 minutes more.",
        detail: "Salmon flakes under light pressure when it's ready. Opaque all the way through means you've gone too far.",
      },
    ],
    notes: [
      "Skin down and don't move it — the skin shields the flesh from the tray and crisps while it does.",
      "50°C in the middle is silky, 60°C is firm. Past 65°C the albumin squeezes out as white curd.",
    ],
    tags: ["traybake", "one-pan", "high-protein"],
    difficulty: "intermediate",
  },
  {
    id: "steak-and-potatoes",
    title: "Steak and potatoes",
    ingredients: ["steak", "potatoes", "butter"],
    minutes: 70,
    serves: 2,
    protein: 52,
    calories: 680,
    diet: "omnivore",
    steps: [
      { text: "Potatoes first — they take an hour, the steak takes ten minutes." },
      {
        text: "Dry the steak thoroughly and salt it.",
        detail: "Surface moisture is the enemy of a crust. Pat it dry, then dry it again just before it goes in.",
      },
      { text: "Very hot pan, don't move it, turn once." },
      {
        text: "Butter in for the last minute, baste, then rest as long as it cooked.",
        detail: "The rest isn't optional. Cut it straight away and the juice is on the board instead of in the steak.",
      },
    ],
    notes: [
      "Rare 50°C, medium-rare 55°C, medium 60°C, pulled 3°C early for carryover.",
      "Over 2cm thick, reverse-sear: low oven to 10°C below target, then a hard sear. Thinner than that, just sear it.",
      "Butter only in the final minute — it burns long before the steak is done.",
    ],
    tags: ["high-protein", "bulk"],
    difficulty: "advanced",
  },

  // ---------------------------------------------------------------- vegan --
  {
    id: "bean-chilli",
    title: "Bean chilli",
    ingredients: ["beans", "tomatoes", "onion", "rice"],
    minutes: 45,
    serves: 4,
    protein: 21,
    calories: 430,
    diet: "vegan",
    steps: [
      { text: "Soften the onion, then fry the spices in the oil for a minute before anything wet goes in." },
      { text: "Tomatoes and beans, simmer half an hour." },
      {
        text: "Over rice.",
        detail: "Thick enough to sit on a spoon. If it's soupy, lid off and let it reduce.",
      },
    ],
    notes: [
      "Frying ground spices transforms them; stirred into liquid they stay dusty and raw-tasting. This is the step that separates a good chilli from a dull one.",
      "Beans plus rice is a complete protein, which matters more here than in any other recipe in this book.",
      "A square of dark chocolate at the end adds depth without tasting of chocolate.",
    ],
    tags: ["vegan", "batch", "cheap", "freezes-well"],
    difficulty: "intermediate",
  },
  {
    id: "lentil-curry",
    title: "Red lentil curry",
    ingredients: ["lentils", "onion", "tomatoes", "rice", "carrots"],
    minutes: 40,
    serves: 4,
    protein: 20,
    calories: 405,
    diet: "vegan",
    steps: [
      { text: "Fry the onion until it colours, then the spices for one minute." },
      { text: "Lentils, diced carrot, tomatoes, water to cover." },
      {
        text: "Simmer 25 minutes.",
        detail: "Stir now and then and check the bottom — lentils sink and catch, and burnt lentil goes through the whole pot.",
      },
    ],
    notes: [
      "Red lentils collapse and thicken the sauce; green ones hold their shape and want ten more minutes. Different dishes, both fine.",
      "Coconut milk for the last five minutes instead of water makes it richer and pushes it to about 500 calories.",
    ],
    tags: ["vegan", "batch", "cheap", "freezes-well"],
    difficulty: "intermediate",
  },
  {
    id: "tofu-rice-bowl",
    title: "Crispy tofu bowl",
    ingredients: ["tofu", "rice", "carrots", "peas"],
    minutes: 30,
    serves: 2,
    protein: 26,
    calories: 470,
    diet: "vegan",
    steps: [
      {
        text: "Press the tofu for twenty minutes, then cube it.",
        detail: "Wet tofu will not crisp, no matter how hot the pan. Something heavy on top of it wrapped in a tea towel is enough.",
      },
      {
        text: "Toss in cornflour, then fry hard in oil until deeply golden on several sides.",
        detail: "The cornflour is what makes the shell. Without it you get warm tofu, which is not the same dish.",
      },
      { text: "Rice, veg, tofu, soy and sesame over the top." },
    ],
    notes: [
      "Extra-firm only. Silken or firm will fall apart in the pan and you'll blame yourself rather than the packet.",
      "An air fryer at 200°C for 15 minutes does the same job with a spoonful of oil instead of a panful.",
    ],
    tags: ["vegan", "bowl", "high-protein"],
    difficulty: "intermediate",
  },
  {
    id: "chickpea-pasta",
    title: "Chickpea tomato pasta",
    ingredients: ["pasta", "chickpeas", "tomatoes", "onion", "olive oil"],
    minutes: 25,
    serves: 3,
    protein: 22,
    calories: 480,
    diet: "vegan",
    steps: [
      { text: "Boil the pasta in well-salted water. Save a mug of it." },
      { text: "Soften the onion, add tomatoes and chickpeas, simmer ten minutes." },
      {
        text: "Pasta in with a splash of the water.",
        detail: "Ready when the oil separates at the edges and the colour goes from bright red to brick.",
      },
    ],
    notes: [
      "Crushing a third of the chickpeas against the pan thickens the sauce and makes it cling — much better than leaving them all whole.",
      "Chickpea or lentil pasta instead of wheat takes this to about 32g a serving.",
    ],
    tags: ["vegan", "cheap", "quick"],
    difficulty: "beginner",
  },
  {
    id: "peanut-noodles",
    title: "Peanut noodles",
    ingredients: ["pasta", "peanut butter", "carrots", "onion"],
    minutes: 20,
    serves: 2,
    protein: 19,
    calories: 520,
    diet: "vegan",
    steps: [
      { text: "Cook the noodles, keep some of the water." },
      {
        text: "Whisk peanut butter with soy, vinegar and hot noodle water until it pours.",
        detail: "Add the water a splash at a time. Too fast and it seizes into a stiff paste that won't come back.",
      },
      { text: "Toss through with the shredded carrot." },
    ],
    notes: [
      "It thickens as it cools, so make it looser than looks right in the pan.",
      "Adding tofu takes it past 30g of protein, which is what this dish is missing on its own.",
    ],
    tags: ["vegan", "quick", "cheap"],
    difficulty: "beginner",
  },

  // --------------------------------------------------------------- soups ---
  {
    id: "chicken-soup",
    title: "Chicken soup",
    ingredients: ["chicken", "carrots", "onion", "rice"],
    minutes: 60,
    serves: 4,
    protein: 34,
    calories: 330,
    diet: "omnivore",
    steps: [
      { text: "Soften the onion and carrot in a big pan." },
      {
        text: "Chicken in, water to cover, gentle simmer for thirty minutes.",
        detail: "Gently. A rolling boil makes the chicken stringy and turns the broth cloudy.",
      },
      { text: "Lift the chicken out, shred it, return it with the rice until tender." },
    ],
    notes: [
      "Skim the grey foam in the first ten minutes and the broth stays clear. Appearance only, but it's free.",
      "Bone-in makes a meaningfully better broth — the collagen is the body you're otherwise missing.",
      "Cook the rice separately if you're batching. Left in overnight it turns the whole pot to porridge.",
    ],
    tags: ["batch", "freezes-well", "cut"],
    difficulty: "intermediate",
  },
  {
    id: "lentil-soup",
    title: "Lentil soup",
    ingredients: ["lentils", "onion", "carrots", "tomatoes"],
    minutes: 45,
    serves: 4,
    protein: 18,
    calories: 290,
    diet: "vegan",
    steps: [
      {
        text: "Soften onion and carrot for ten minutes.",
        detail: "This base does most of the flavour work. Rush it and you get a thin soup that tastes only of lentils.",
      },
      { text: "Lentils and tomatoes, water to cover by a couple of centimetres." },
      { text: "Simmer 25–30 minutes, season at the end." },
    ],
    notes: [
      "Salt at the end, not the start — the volume drops as it cooks and early salting leaves it over-seasoned.",
      "Acid is what stops it tasting flat: lemon or vinegar, a teaspoon at a time until it lifts.",
    ],
    tags: ["vegan", "batch", "cheap", "freezes-well", "cut"],
    difficulty: "beginner",
  },

  // ---------------------------------------------------------------- quick --
  {
    id: "chicken-wrap",
    title: "Chicken wrap",
    ingredients: ["chicken", "bread", "yoghurt", "lettuce"],
    minutes: 12,
    serves: 1,
    protein: 45,
    calories: 450,
    diet: "omnivore",
    steps: [
      { text: "Sear the sliced chicken with paprika and salt." },
      {
        text: "Yoghurt, garlic and lemon whisked together for the sauce.",
        detail: "Greek yoghurt doing mayo's job again — a fifth of the calories and it adds protein rather than none.",
      },
      { text: "Wrap it all up." },
    ],
    notes: [
      "Warm the wrap for twenty seconds in the dry pan first; cold ones split when you roll them.",
      "This is the single highest protein-per-calorie thing in the book, which is why it survives every cut.",
    ],
    tags: ["quick", "high-protein", "cut"],
    difficulty: "beginner",
  },
  {
    id: "cheese-toastie",
    title: "Cheese toastie",
    ingredients: ["bread", "cheese", "butter"],
    minutes: 10,
    serves: 1,
    protein: 22,
    calories: 480,
    diet: "vegetarian",
    steps: [
      {
        text: "Butter the outsides, cheese in the middle.",
        detail: "Outsides, not insides. The butter is there to fry the bread, not to sit inside it.",
      },
      {
        text: "Medium-low heat, pressing down, until golden. Flip.",
        detail: "Hot and fast gives you burnt bread and cheese that hasn't moved.",
      },
    ],
    notes: [
      "Grate the cheese. A slice melts unevenly and slides out; grated melts through and stays.",
      "A lid for the first minute traps steam and gets the cheese going before the bread is done.",
    ],
    tags: ["quick", "cheap", "bulk"],
    difficulty: "beginner",
  },
  {
    id: "scrambled-eggs",
    title: "Scrambled eggs on toast",
    ingredients: ["eggs", "butter", "bread"],
    minutes: 10,
    serves: 1,
    protein: 26,
    calories: 400,
    diet: "vegetarian",
    steps: [
      {
        text: "Beat the eggs with a good pinch of salt.",
        detail: "Salt them now, not at the end — it has time to dissolve and the texture is better for it.",
      },
      {
        text: "Butter in a cold pan, eggs in, stir over a low heat.",
        detail: "Low really means low. High heat gives you rubbery, weeping eggs in under a minute and there's no coming back.",
      },
      {
        text: "Off the heat while still glossy.",
        detail: "They keep cooking for another twenty seconds. Done in the pan means dry on the plate.",
      },
    ],
    notes: [
      "A splash of cream off the heat stops the carryover cooking dead and makes them silkier.",
      "Curd size is a function of stirring: constant gives small and custardy, folding every ten seconds gives large and soft.",
    ],
    tags: ["breakfast", "quick"],
    difficulty: "beginner",
  },
];
